import type { KeyValueObject } from "$/lib/types/Graphs.types";
import {
  addDays,
  differenceInDays,
  getYear,
  parse,
  startOfMonth,
  startOfYear,
  subMonths
} from "date-fns";
import { fr } from "date-fns/locale";
import { customColors } from "tailwind.config";
import { format, getLast6MonthsStartAndEndDates } from "../date.functions";

export const handleOnChangeDefaultGraphsTabs = (
  tabId: string,
  handleOnChangeDateRange: (values: [Date, Date]) => void,
) => {
  const today = new Date();

  switch (tabId) {
    case "CURRENT_MONTH": {
      const startOfCurrentMonth = startOfMonth(today);
      const endOfCurrentMonth = today;

      handleOnChangeDateRange([startOfCurrentMonth, endOfCurrentMonth]);
      break;
    }

    case "LAST_MONTH": {
      const startOfPreviousMonth = startOfMonth(
        new Date(new Date().setMonth(new Date().getMonth() - 1)),
      );

      handleOnChangeDateRange([startOfPreviousMonth, new Date()]);
      break;
    }

    case "LAST_3_MONTHS": {
      const startOf3MonthsAgo = subMonths(today, 3);
      const endOfCurrentMonth = new Date();

      handleOnChangeDateRange([startOf3MonthsAgo, endOfCurrentMonth]);
      break;
    }

    case "LAST_6_MONTHS": {
      const { start, end } = getLast6MonthsStartAndEndDates();

      handleOnChangeDateRange([start, end]);
      break;
    }

    case "CURRENT_YEAR": {
      const startOfCurrentYear = startOfYear(today);
      const endOfCurrentYear = today;

      handleOnChangeDateRange([startOfCurrentYear, endOfCurrentYear]);
      break;
    }
  }
};

export const handleGenerateYearReferences = (
  data: { [x: string]: string | number | [number, number] | null }[],
  key: string,
  format: string,
) => {
  const years: { value: number; label: string }[] = [];

  data.forEach((item) => {
    if (!item[key] || typeof item[key] === "number" || Array.isArray(item[key]))
      return;

    const currentYear = getYear(
      parse(item[key], format, new Date(), { locale: fr }),
    );

    const yearExists = years.some((year) => year.value === currentYear);

    if (!yearExists) years.push({ value: currentYear, label: item[key] });
  });

  return years;
};

export const ELECTRICITY_COLORS = [
  customColors.primary.DEFAULT,
  customColors.secondary.DEFAULT,
  "#00FFFF",
  "#00E5FF",
  "#1DE9B6",
  "#00C853",
  "#64DD17",
  "#AEEA00",
  "#FFD600",
  "#FFAB00",
  "#FF6D00",
  "#FF3D00",
  "#00B0FF",
  "#0091EA",
  "#2962FF",
  "#651FFF",
  "#D500F9",
  "#00FFC8",
  "#00F9FF",
  "#33BFFF",
  "#66FF66",
  "#B2FF59",
  "#DCE775",
  "#FFF176",
  "#FFB74D",
  "#FF8A65",
  "#FF5252",
];

export const GAS_COLORS = [
  customColors.forest.DEFAULT,
  "#FF6F00",
  "#FFA000",
  "#FFC107",
  "#FFD54F",
  "#FFE082",
  "#FF8F00",
  "#FF5722",
  "#FF7043",
  "#F4511E",
  "#E64A19",
  "#D84315",
  "#BF360C",
  "#FFAB91",
  "#FFCCBC",
  "#D7CCC8",
  "#BCAAA4",
  "#A1887F",
  "#8D6E63",
  "#795548",
  "#6D4C41",
  "#FFB300",
  "#F57C00",
  "#FF9800",
  "#FBC02D",
  "#FFF59D",
];

export const DEFAULT_GRAPH_COLORS = [
  customColors.primary.DEFAULT,
  customColors.secondary.DEFAULT,
  customColors.forest.DEFAULT,
  "#FFC107",
  "#FF5722",
  "#673AB7",
  "#3F51B5",
  "#009688",
  "#8BC34A",
  "#FF9800",
  "#795548",
  "#607D8B",
  "#FFEB3B",
  "#03A9F4",
  "#9C27B0",
  "#E91E63",
  "#4CAF50",
  "#FF5722",
  "#FFC107",
  "#673AB7",
  "#3F51B5",
  "#009688",
  "#8BC34A",
  "#FF9800",
  "#795548",
  "#607D8B",
  "#FFEB3B",
  "#03A9F4",
];

export const BASE_ELEC_COLORS = [
  "#183E7A",
  "#5881C1",
  "#84BCAF",
  "#3D6F9F98",
  "#6A98D198",
  "#99C9B998",
  "#558DB698",
  "#7EADDC98",
  "#A9D3C398",
  "#6D9EC198",
  "#91B9E298",
  "#B9DDCD98",
  "#88B4D298",
  "#A4C4E798",
  "#C9E7D798",
  "#A3C8E298",
  "#B8D0EC98",
  "#D9F1E198",
  "#CBE2F198",
  "#E9FBEC98",
];

export const PEAK_ELEC_COLORS = [
  "#784b7a",
  "#ad79a2",
  "#f49ba7",
  "#9F6F9E98",
  "#B98DAF98",
  "#F5A8B398",
  "#B282B098",
  "#C59FB998",
  "#F7B6C098",
  "#C495C298",
  "#D2B1C398",
  "#F9C3CC98",
  "#D6A8D498",
  "#E0C3CD98",
  "#F9D1D998",
  "#E8BAD698",
  "#EEC5D798",
  "#F9D7E198",
  "#F9DCE298",
  "#F9E9EC98",
];

export const PEG_GAS_COLORS = [
  "#2e6d38",
  "#67a158",
  "#b3c365",
  "#3A7A4D98",
  "#78B86B98",
  "#C5D57898",
  "#4C8C5D98",
  "#8AC77E98",
  "#D7E28A98",
  "#60A46D98",
  "#A3D28F98",
  "#E9EC9C98",
  "#7BAF8498",
  "#B4DF9F98",
  "#F2F5A698",
  "#98C89598",
  "#C5E8B098",
  "#F8F9B898",
  "#B4E3A698",
  "#D6F2C198",
  "#FBF9D098",
];

export const TTF_GAS_COLORS = [
  "#a31c1f",
  "#d9252a",
  "#ef5f53",
  "#B73A3C98",
  "#E33D3F98",
  "#F7706A98",
  "#C85B5D97",
  "#F24E5297",
  "#F98D7B97",
  "#D97C7D96",
  "#F76A6D96",
  "#FBAA8C96",
  "#EA9D9E95",
  "#F98B8F95",
  "#FDC6A095",
  "#F1B0B198",
  "#FBB2B598",
  "#FDD7B198",
  "#F8C2C398",
  "#FBC3C698",
  "#FDE1C398",
];

export const CONTAINER_HEIGHT_PERCENTAGE = 0.75;
export const MID_CONTAINER_HEIGHT_PERCENTAGE = 0.6;

export const CHART_HEIGHT_PERCENTAGE = 0.58;

/**
 * Formats a value in watt-hours (Wh) to the appropriate unit (kWh, MWh, GWh, TWh).
 * @param value - The value in watt-hours (Wh) to be formatted.
 * @returns A formatted string with the value and the appropriate unit.
 */
export function formatEnergy(value: number | string, withUnit = false) {
  const val = +(+value).toFixed(2);
  if (val < 1e3) {
    return val;
  } else if (val < 1e6) {
    return (val / 1e3).toFixed(2) + (withUnit ? "K" : "");
  } else if (val < 1e9) {
    return (val / 1e6).toFixed(2) + (withUnit ? "M" : "");
  } else if (val < 1e12) {
    return (val / 1e9).toFixed(2) + (withUnit ? "G" : "");
  } else {
    return (val / 1e12).toFixed(2) + (withUnit ? "T" : "");
  }
}

export const generateYAxisTicks = (
  min: number,
  max: number,
  numberOfTicks = 12,
) =>
  [
    ...Array.from(
      new Set(
        Array.from({ length: numberOfTicks }, (_, i) => {
          if (i === 0) return min;
          if (i === numberOfTicks - 1) return max;

          const step = (max - min) / (numberOfTicks - 2);

          return Math.round(min + step * i);
        }),
      ),
    ),
  ].sort();

export const generateXAxisTicks = (
  start: Date,
  end: Date,
  formatStr = "dd/MM/yy",
) => {
  const totalDays = differenceInDays(end, start);

  const tickCount = totalDays < 12 ? 12 : totalDays < 60 ? 8 : 12;
  const interval = totalDays / (tickCount - 1);

  const ticks: string[] = [];

  for (let i = 0; i < tickCount; i++) {
    const tickDate = addDays(start, Math.round(i * interval));
    ticks.push(format(tickDate, formatStr));
  }

  ticks[0] = format(start, formatStr);
  ticks[ticks.length - 1] = format(end, formatStr);

  const uniqueTicks = [...new Set(ticks)];

  return uniqueTicks;
};

export const generateGraphDateRangeFromData = (
  data: KeyValueObject[],
  formatStr: string,
  xAxisDataKey: string,
) => {
  const firstElement = data[0]?.[xAxisDataKey];
  const lastElement = data[data.length - 1]?.[xAxisDataKey];

  if (
    !firstElement ||
    !lastElement ||
    typeof firstElement !== "string" ||
    typeof lastElement !== "string"
  )
    return;

  const start = parse(firstElement, formatStr, new Date(), { locale: fr });

  const end = parse(lastElement, formatStr, new Date(), { locale: fr });

  return [start, end];
};
